import axios from 'axios';

const axiosServices = axios.create({ baseURL: process.env.REACT_APP_API_ENDPOINT_V2 || 'http://localhost:3000/' });

// Token helpers (localStorage based)
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

const getAccessToken = (): string | null => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
};

const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setAccessToken = (token: string) => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch {}
};

const setRefreshToken = (token: string) => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {}
};

const clearTokens = () => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {}
};

axiosServices.interceptors.request.use(
  (config) => {
    const access = getAccessToken();
    if (access) {
      config.headers['Authorization'] = `Bearer ${access}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh handling
let isRefreshing = false as boolean;
let pendingQueue: Array<(token: string | null) => void> = [];

const processQueue = (newToken: string | null) => {
  pendingQueue.forEach((cb) => cb(newToken));
  pendingQueue = [];
};

axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Feature-flag gating returns 401 with a "feature" keyword in the detail.
    // These are NOT expired-token 401s — skip the refresh/redirect cycle and
    // let the calling hook handle the empty-content render.
    const featureGateDetail = error?.response?.data?.errors?.detail;
    const isFeatureGate =
      error?.response?.status === 401 &&
      typeof featureGateDetail === "string" &&
      featureGateDetail.toLowerCase().includes("feature");
    if (isFeatureGate) {
      return Promise.reject(error);
    }

    if (
      error?.response?.status === 401 &&
      !window.location.href.includes('/signin') &&
      !originalRequest?._retry
    ) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        window.location.pathname = '/signin';
        return Promise.resolve();
      }

      // Mark this request so we don't attempt another refresh cycle
      // if it still returns 401 after retrying with a fresh access token
      // (i.e. the 401 is a permission issue, not an expired token).
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue the request until refresh is done
        return new Promise((resolve, reject) => {
          pendingQueue.push((newToken) => {
            if (!newToken) {
              reject(error);
              return;
            }
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(axiosServices(originalRequest));
          });
        });
      }

      isRefreshing = true;

      const base = process.env.REACT_APP_API_ENDPOINT_V2 || '';
      // Allow override via env. Defaults to 'refresh_token' path appended to base.
      const refreshPath = process.env.REACT_APP_API_REFRESH_ENDPOINT_V2 || 'refresh';

      try {
        const res = await axios.post(`${base}${refreshPath}`, {
          refresh_token: refreshToken,
          email: JSON.parse(localStorage.getItem('user') || '{}')?.email,
        });

        const newAccess: string = res?.data?.access_token;
        const newRefresh: string = res?.data?.refresh_token;

        if (newAccess) setAccessToken(newAccess);
        if (newRefresh) setRefreshToken(newRefresh);

        processQueue(newAccess);

        if (newAccess) {
          originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
        }

        return axiosServices(originalRequest);
      } catch (refreshErr) {
        clearTokens();
        processQueue(null);
        window.location.pathname = '/signin';
        
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // In production, let app handle it
    return Promise.reject(error);
  }
);

export default axiosServices;
